import React, { useCallback, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../hook';
import { fetchCustomers, fetchDevices, fetchEvents } from './_slice';
import { Card, Col, Modal, Row, Space, Table, TableProps, Tag } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { appConfig } from '../../common/config';
import LayoutStandard from '../../layouts/layout_standard';
import { EnumDeviceSearchType, IEventSearchParams } from '../../common/models';
import CopyToClipboard from '../../components/copy_clipboard';
import { convertDayjsToISOString, formatDateTime, getTimeRangeFromNow } from '../../common/utils';
import EventSearchForm from './features/event_search_form';
import {
  convertEventsToCSV,
  getEventChartTitle,
  getEventFileName,
  isLevelSenseDevice,
  isPeopleSenseDevice,
  convertEvents,
  findDevice,
} from '../../common/helper';
import store from '../../store';
import EventAmCharts from './features/event_amcharts';
import DeviceInstallation from '../device/install/device_installation';
import DeviceDetails from './features/device_details';

const commonColumns1: TableProps<any>['columns'] = [
  {
    title: 'Event Time',
    dataIndex: 'eventTime',
    key: 'eventTime',
    render: formatDateTime,
  },
];

const levelSenseColumns: TableProps<any>['columns'] = [
  {
    title: 'Level (%)',
    dataIndex: 'level',
    key: 'level',
  },
  {
    title: 'Ultrasonic Distance (cm)',
    dataIndex: 'ultrasonicDistance',
    key: 'ultrasonicDistance',
  },
];

const peopleSenseColumns: TableProps<any>['columns'] = [
  {
    title: 'Total Count',
    dataIndex: 'totalCount',
    key: 'totalCount',
  },
  {
    title: 'Count',
    dataIndex: 'count',
    key: 'count',
    render: (val, record) => {
      if (record.index === undefined) {
        return val;
      }
      return <>
        <Tag color="blue">{record.index}</Tag> {val}
      </>;
    },
  },
];

const commonColumns2: TableProps<any>['columns'] = [
  {
    title: 'Device Name',
    dataIndex: 'deviceName',
    key: 'deviceName',
  },
  {
    title: 'Device Type',
    dataIndex: 'deviceType',
    key: 'deviceType',
  },
];

const commonColumns3: TableProps<any>['columns'] = [
  {
    title: 'RSSI',
    dataIndex: 'rssi',
    key: 'rssi',
    render: (val, _) => {
      const status = appConfig.getRssiStatus(val);
      if (!status) return null;
      return (
        <>
          {val} <Tag color={status.color}>{status.text}</Tag>
        </>
      );
    },
  },
  {
    title: 'Temperature (℃)',
    dataIndex: 'temperature',
    key: 'temperature',
  },
  {
    title: 'Battery (V)',
    dataIndex: 'voltage',
    key: 'voltage',
    render: (val, _) => {
      const status = appConfig.getBatteryStatus(val);
      if (!status) return null;
      return (
        <>
          {val} <Tag color={status.color}>{status.text}</Tag>
        </>
      );
    },
  },
];

interface RouteParams {
  searchType: EnumDeviceSearchType;
  keywords: string;
  [key: string]: string;
}

interface IEventsPageProps {
}

const EventsPage: React.FC<IEventsPageProps> = () => {
  const customerState = useAppSelector((state) => state.customer);
  const dispatch = useAppDispatch();
  const { searchType, keywords } = useParams<RouteParams>();
  const [searchParams, setSearchParams] = useState<IEventSearchParams>({
    searchType: searchType as EnumDeviceSearchType ?? EnumDeviceSearchType.customerName,
    keywords: keywords ?? '',
  });
  const [isInstallDeviceOpen, setIsInstallDeviceOpen] = useState(false);
  const [deviceSerialToInstall, setDeviceSerialToInstall] = useState<string>('');

  // Function to fetch events and devices based on search params
  const fetchEventsData = useCallback(async (params: IEventSearchParams) => {
    await dispatch(fetchDevices(params));
    await dispatch(fetchEvents(params));
  }, [dispatch]);

  // Function to export events to CSV
  const exportToCSV = useCallback(async (params: IEventSearchParams) => {
    // Please don't use `customerState.events` here based on the following:
    // 1. dispatch(fetchEvents) triggers a thunk that starts an async request and returns a Promise.
    // 2. The `await` waits for the thunk to finish, and the store may already be updated by the fulfilled reducer.
    // 3. However, the customerState you get from useSelector is a snapshot from the current render, so it won’t change within the same function call.
    // 4. React will perform the next render with the updated store, and only then will useSelector return the new events.
    const customer = store.getState().customer;
    const devices = customer.devices;

    const currDevice = findDevice(params.searchType, params.keywords, devices);
    const events = convertEvents(devices, customer.events, !!currDevice); // Don't use customerState.events here
    const csv = convertEventsToCSV(events, !!currDevice, isLevelSenseDevice(currDevice), isPeopleSenseDevice(currDevice));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', getEventFileName(params, customer.customers, events?.[0]));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Function to handle search or export
  const onSearchOrExport = useCallback(async (params: IEventSearchParams, isExport: boolean) => {
    setSearchParams(params);
    if (params.timeRange) {
      params.startTime = convertDayjsToISOString(params.timeRange?.[0]);
      params.endTime = convertDayjsToISOString(params.timeRange?.[1]);
    }
    await fetchEventsData(params);
    if (isExport) exportToCSV(params);
  }, [fetchEventsData, exportToCSV]);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (!customerState.customers.length) return;
    const newSearchType = searchType || EnumDeviceSearchType.customerName;
    const newKeywords = newSearchType === EnumDeviceSearchType.customerName && !keywords
      ? customerState.customers[0].Name
      : keywords;

    // TODO: There is an issue we can fix in the future.
    // Because if the device has not reported inthe past 7 days then the UI looks weird as their is no data.
    // Maybe we do the end time is always today, but the start time is 7 days before the most recent message.
    // This means most devices will show 7 days, but devices having issues will clearly show when it stopped reporting,
    // and you can see big blank space to make it clear that it has stopped reporting.
    const newTimeRange = [
      EnumDeviceSearchType.deviceIdSerial,
      EnumDeviceSearchType.networkId,
    ].includes(newSearchType as EnumDeviceSearchType)
      ? getTimeRangeFromNow(7)
      : undefined;

    onSearchOrExport({
      searchType: newSearchType,
      keywords: newKeywords,
      timeRange: newTimeRange,
    }, false);

  }, [customerState.customers, onSearchOrExport, searchType, keywords]);

  const openInstallDevice = (deviceSerial: string) => {
    setDeviceSerialToInstall(deviceSerial);
    setIsInstallDeviceOpen(true);
  };

  const closeInstallDevice = () => {
    setIsInstallDeviceOpen(false);
  };

  const viewDeviceEvents = (deviceSerial: string) => {
    // Navigate to events page with Device ID / Serial search
    // Using window.location.href instead of react-router's navigate to force a full reload
    // The url cannot contain "#", otherwise the routing will not work as expected.
    window.location.href = `${window.location.origin}/events/searchType/Device ID or Serial/keywords/${deviceSerial}`;
  };

  // Current device
  const currDevice = findDevice(searchParams.searchType, searchParams.keywords, customerState.devices);

  // Convert events
  let events = convertEvents(customerState.devices, customerState.events, !!currDevice);

  // Merge columns
  const columns = [...commonColumns1];
  if (currDevice) {
    if (isLevelSenseDevice(currDevice)) columns.push(...levelSenseColumns);
    if (isPeopleSenseDevice(currDevice)) columns.push(...peopleSenseColumns);
  } else {
    columns.push({
      title: 'Device Serial',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      render: (val) => <><Link to='#' onClick={() => viewDeviceEvents(val)}><b>{val}</b></Link> <CopyToClipboard content={val} /></>,
    });
    columns.push(...commonColumns2);
    columns.push(...levelSenseColumns);
    columns.push(...peopleSenseColumns);
  }
  columns.push(...commonColumns3);

  return (
    <LayoutStandard page={appConfig.pages.events} count={events.length}>
      <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
        <Card size='small'>
          <EventSearchForm
              searchParams={searchParams}
              customers={customerState.customers}
              onSearch={(params) => onSearchOrExport(params, false)}
              onExport={(params) => onSearchOrExport(params, true)}
            />
        </Card>
        {
          // Event chart is only available when viewing an individual device, and the device is Level/PeopleSense
          currDevice && (isLevelSenseDevice(currDevice) || isPeopleSenseDevice(currDevice)) && (
            <Card size='small' title={`${getEventChartTitle(currDevice)}`} headStyle={{ textAlign: 'center' }}>
              <Row gutter={24} wrap>
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={20}
                >
                  <EventAmCharts events={events} device={currDevice} />
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={4}
                >
                  {
                    <DeviceDetails device={currDevice} onInstall={openInstallDevice}></DeviceDetails>
                  }
                </Col>
              </Row>
            </Card>
          )
        }
        <Table<any>
          columns={columns}
          dataSource={events}
          rowKey='key'
          pagination={{ position: ['bottomCenter'] }}
          loading={customerState.isLoadingEvents || customerState.isLoadingDevices}
          scroll={{ x: 'max-content' }}
        />
      </Space>
      <Modal
        title='Install Device'
        open={isInstallDeviceOpen}
        onCancel={closeInstallDevice}
        footer={null}
        width={'96%'}
        // If destroyOnClose is true, the modal is destroyed when closed; otherwise, it will be just hidden.
        // Keeping it not destoryed may cause unexpected behaviors.
        destroyOnClose={true}
        style={{ top: 10, maxWidth: 1600 }}
        className='install-device-modal'
      >
        <DeviceInstallation
          deviceIdOrSerial={deviceSerialToInstall}
          onSaved={() => {closeInstallDevice()}}
          onCanceled={() => {closeInstallDevice()}}
        />
      </Modal>
    </LayoutStandard>
  );
};

export default EventsPage;
